import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  IconButton
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { IP } from '../configuracion.js';

const BASE_API_URL = `${IP}/tavpos/backend`;

const AjusteChequeForm = ({ numeroCheque, property, onSuccess, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chequeData, setChequeData] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [ajustes, setAjustes] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [formData, setFormData] = useState({
    id: '',
    tipoRegistro: 'pago',
    nombrePago: '',
    monto: '',
    tipoAjuste: '',
    propina: '',
    cambio: '',
    folio: '',
    motivo: '',
    usuario_ajuste: 'admin'
  });

  const formatCurrency = (value) => {
    const numericValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(numericValue);
  };

  const cargarMetodosPago = async (pagosData) => {
    try {
      const response = await fetch(`${BASE_API_URL}/data/${encodeURIComponent(property)}/payments.json`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Error al cargar métodos de pago: ${response.statusText}`);
      }
      const metodos = await response.json();
      setMetodosPago(metodos);
      if (!formData.nombrePago && metodos.length > 0) {
        const metodoExistente = pagosData.find(p => metodos.some(m => m.name === p.nombre_pago));
        setFormData(prev => ({
          ...prev,
          nombrePago: metodoExistente ? metodoExistente.nombre_pago : metodos[0].name
        }));
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError(`Error al cargar métodos de pago: ${err.message}`);
    }
  };

  const cargarAjustes = async () => {
    try {
      const response = await fetch(
        `${BASE_API_URL}/ajuste_cheque.php?property=${encodeURIComponent(property)}&cheque=${numeroCheque}&tipo=1`,
        {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        setAjustes(result.data.ajustes || []);
      } else {
        throw new Error(result.message || 'Error en la respuesta del servidor');
      }
    } catch (err) {
      console.error('Error al cargar ajustes:', err);
      setError(`Error al cargar ajustes: ${err.message}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!numeroCheque || !property) {
        console.warn('Missing numeroCheque or property', { numeroCheque, property });
        setError('Faltan parámetros: número de cheque o propiedad');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('Fetching cheque data for:', { numeroCheque, property });

        // Fetch cheque and payments from lectura_cheques_corte.php
        const chequeResponse = await fetch(
          `${BASE_API_URL}/lectura_cheques_corte.php?property=${encodeURIComponent(property)}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'include'
          }
        );

        if (!chequeResponse.ok) {
          throw new Error(`HTTP error ${chequeResponse.status}: ${chequeResponse.statusText}`);
        }

        const chequeResult = await chequeResponse.json();

        if (chequeResult.success && chequeResult.data && Array.isArray(chequeResult.data.cheques)) {
          const cheque = chequeResult.data.cheques.find(c => c.numero_cheque === numeroCheque || c.id === numeroCheque);
          if (cheque) {
            setChequeData({
              numero_cheque: cheque.numero_cheque,
              total_cheque: cheque.total_cheque,
              total_pagado: cheque.total_pagado,
              fecha_control: cheque.fecha_control
            });
            const pagosData = cheque.pagos || [];
            setPagos(pagosData);
            await cargarMetodosPago(pagosData);
          } else {
            throw new Error(`No se encontró el cheque ${numeroCheque}`);
          }
        } else {
          throw new Error(chequeResult.message || 'Error en la respuesta del servidor');
        }

        // Cargar ajustes
        await cargarAjustes();

      } catch (err) {
        console.error('Fetch error details:', {
          message: err.message,
          stack: err.stack,
          url: `${BASE_API_URL}/lectura_cheques_corte.php?property=${encodeURIComponent(property)}`
        });
        setError(`Error al cargar datos: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [numeroCheque, property]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const tipoOperacion = formData.id ? 2 : 3; // 2 para update, 3 para insert
      const monto = parseFloat(formData.monto) || 0;
      const propina = parseFloat(formData.propina) || 0;
      const cambio = parseFloat(formData.cambio) || 0;

      const requestBody = {
        numero_cheque: numeroCheque,
        ajustes: [
          {
            id_ajuste: formData.id || `ajuste_${Date.now()}`,
            nombre_pago: formData.nombrePago,
            monto_ajuste: monto,
            tipo_ajuste: formData.tipoAjuste,
            motivo: formData.motivo || '',
            usuario_ajuste: formData.usuario_ajuste
          }
        ]
      };

      const response = await fetch(
        `${BASE_API_URL}/ajuste_cheque.php?property=${encodeURIComponent(property)}&cheque=${numeroCheque}&tipo=${tipoOperacion}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Property': property
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      const result = await response.json();
      if (result.success) {
        setFormData({
          id: '',
          tipoRegistro: 'pago',
          nombrePago: metodosPago[0]?.name || '',
          monto: '',
          tipoAjuste: '',
          propina: '',
          cambio: '',
          folio: '',
          motivo: '',
          usuario_ajuste: 'admin'
        });
        await cargarAjustes(); // Recargar los ajustes después de guardar
        onSuccess();
      } else {
        throw new Error(result.message || 'Error al guardar el ajuste');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(`Error al guardar: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ajuste) => {
    setFormData({
      id: ajuste.id_ajuste,
      tipoRegistro: 'ajuste',
      nombrePago: ajuste.nombre_pago,
      monto: ajuste.monto_ajuste,
      tipoAjuste: ajuste.tipo_ajuste,
      motivo: ajuste.motivo || '',
      usuario_ajuste: ajuste.usuario_ajuste,
      propina: '',
      cambio: '',
      folio: ''
    });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${BASE_API_URL}/ajuste_cheque.php?property=${encodeURIComponent(property)}&cheque=${numeroCheque}&tipo=2`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Property': property
          },
          credentials: 'include',
          body: JSON.stringify({
            numero_cheque: numeroCheque,
            ajustes: [
              { id_ajuste: id, monto_ajuste: 0, motivo: 'Eliminado' }
            ]
          })
        }
      );

      const result = await response.json();
      if (result.success) {
        setAjustes(ajustes.filter(a => a.id_ajuste !== id));
        onSuccess();
      } else {
        throw new Error(result.message || 'Error al eliminar');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(`Error al eliminar: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 800 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Cargando datos...</Typography>
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {chequeData && (
            <Box mb={3}>
              <Typography variant="h6">Detalles del Cheque #{numeroCheque}</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Total Cheque</TableCell>
                      <TableCell>Total Pagado</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Diferencia</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{formatCurrency(chequeData.total_cheque)}</TableCell>
                      <TableCell>{formatCurrency(chequeData.total_pagado)}</TableCell>
                      <TableCell>{chequeData.fecha_control || 'N/A'}</TableCell>
                      <TableCell>
                        {formatCurrency(
                          (parseFloat(chequeData.total_cheque) || 0) -
                          (parseFloat(chequeData.total_pagado) || 0)
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box mb={3}>
            <Typography variant="h6">Ajustes Registrados</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Método</TableCell>
                    <TableCell>Monto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Motivo</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ajustes.length > 0 ? (
                    ajustes.map((ajuste) => (
                      <TableRow key={ajuste.id_ajuste}>
                        <TableCell>{ajuste.nombre_pago}</TableCell>
                        <TableCell>{formatCurrency(ajuste.monto_ajuste)}</TableCell>
                        <TableCell>{ajuste.tipo_ajuste}</TableCell>
                        <TableCell>{ajuste.motivo || 'N/A'}</TableCell>
                        <TableCell>{ajuste.usuario_ajuste}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleEdit(ajuste)}>
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(ajuste.id_ajuste)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6}>No hay ajustes registrados</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6">Registrar Ajuste</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Método de Pago</InputLabel>
              <Select
                name="nombrePago"
                value={formData.nombrePago}
                onChange={handleInputChange}
                disabled={metodosPago.length === 0}
              >
                {metodosPago.map((metodo) => (
                  <MenuItem key={metodo.id} value={metodo.name}>
                    {metodo.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Monto"
              name="monto"
              value={formData.monto}
              onChange={handleInputChange}
              type="number"
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de Ajuste</InputLabel>
              <Select
                name="tipoAjuste"
                value={formData.tipoAjuste}
                onChange={handleInputChange}
              >
                <MenuItem value="correccion">Corrección</MenuItem>
                <MenuItem value="cancelacion">Cancelación</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Motivo"
              name="motivo"
              value={formData.motivo}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            />
            
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button variant="contained" type="submit" disabled={loading}>
                {formData.id ? 'Actualizar' : 'Registrar'}
              </Button>
              <Button variant="outlined" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

AjusteChequeForm.propTypes = {
  numeroCheque: PropTypes.string.isRequired,
  property: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AjusteChequeForm;